import { Group, Person } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Grid,
  ListItem,
  ListItemText,
  MenuItem,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { useAlert } from '../context/AlertContext';
import { LoadingDialog } from '../core/LoadingDialog';
import { NoDataDialog } from '../core/NoDataDialog';
import { useGetLegalRoleTemplates } from '../hooks/reactQuerys/LegalRoleQueries';
import {
  useCreateLegalTransactionFullObject,
  useGetLegalTransactionTypeFullObject,
} from '../hooks/reactQuerys/LegalTransactionQueries';
import { ILegalClient } from '../Interfaces/ILegalClient';
import { ILegalRole } from '../Interfaces/ILegalRole';
import { ILegalTransactionFullObject } from '../Interfaces/ILegalTransaction';
import { type ILegalTransactionType } from '../Interfaces/ILegalTransactionType';
import { IUser } from '../Interfaces/IUser';
import { IUserGroup } from '../Interfaces/IUserGroup';
import { LegalTransactionClientsOverview } from './LegalTransactionClientsOverview';
import { handleInvoiceReminder } from './utilities';
import { CustomeTextField } from '../generic/CustomeTextField';
import { IArnoTopObject } from '../Interfaces/IArnoTopObject';
import { IConfig } from '../Interfaces/IConfig';

interface IArnoTopLocalSync {
  enableLegalTransaction: boolean;
  enableLegalClients: boolean;
  idArnoTopObject: number;
}

interface IProps {
  config: IConfig;
  users: IUser[];
  userGroups: IUserGroup[];
  legalTransactionTypes: ILegalTransactionType[];
  arnoTopObjects: IArnoTopObject[] | undefined;
  handleClose: (isEdited: boolean) => void;
  handleFinished: (idLegalTransaction: number) => void;
}

export const LegalTransactionNew = ({
  config,
  users,
  userGroups,
  legalTransactionTypes,
  arnoTopObjects,
  handleClose,
  handleFinished,
}: IProps) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const {
    createLegalTransactionFullObject,
    isPendingCreateLegalTransactionFullObject,
  } = useCreateLegalTransactionFullObject();

  const { roleTemplates, isLoadingRoleTemplates, refetchRoleTemplates } =
    useGetLegalRoleTemplates();

  const [selectedType, setSelectedType] = useState('user');
  const [idLegalTransactionType, setIdLegalTransactionType] = useState(0);

  const [arnoSync, setArnoSync] = useState<IArnoTopLocalSync>({
    enableLegalClients: false,
    enableLegalTransaction: false,
    idArnoTopObject: -1,
  });

  const { legalTransactionTypeFullObject } =
    useGetLegalTransactionTypeFullObject(idLegalTransactionType);

  const { showAlert } = useAlert();

  const initDefaultEmployeeIdUser = () => {
    const idUser = Number(localStorage.getItem('idUser'));

    if (
      users
        .filter(user => Boolean(user.isNotary) === false)
        .map(user => user.idUser)
        .indexOf(idUser) > -1
    ) {
      return idUser;
    } else {
      return null;
    }
  };

  const initDefaultUser = () =>
    users.filter(u => Boolean(u.isNotary)).length === 1
      ? users.filter(u => Boolean(u.isNotary))[0].idUser
      : null;

  const combinedOptions = [
    ...users
      .filter(user => !user.isNotary && !user.isBlocked)
      .map(user => ({
        type: 'Mitarbeiter',
        id: user.idUser,
        name: `${user.FirstName} ${user.LastName}`,
      })),
    ...userGroups.map(group => ({
      type: 'Gruppe',
      id: group.idUserGroup,
      name: group.UserGroup,
    })),
  ];

  const [title, setTitle] = useState('');
  const [idUser, setIdUser] = useState(initDefaultUser());

  const [employeeIdUser, setEmployeeIdUser] = useState<number | null>(
    initDefaultEmployeeIdUser
  );
  const [userGroupId, setUserGroupId] = useState<number | null>(null);
  const [takenLegalClients, setTakenLegalClients] = useState<ILegalClient[]>(
    []
  );

  const availableRoles: ILegalRole[] = useMemo(
    () =>
      roleTemplates
        ?.filter(r => r.idLegalTransactionType === idLegalTransactionType)
        .map(r => ({
          ...r,
          idLegalRole: r.idLegalRoleTemplate,
          idLegalTransaction: -1,
          LegalRole: r.LegalRoleTemplate,
        })) || [],
    [idLegalTransactionType, roleTemplates]
  );

  const isEdited = useMemo(
    () =>
      title !== '' ||
      initDefaultEmployeeIdUser() !== employeeIdUser ||
      initDefaultUser() !== idUser ||
      userGroupId !== null ||
      takenLegalClients.length > 0 ||
      idLegalTransactionType !== 0,
    [
      takenLegalClients.length,
      employeeIdUser,
      idUser,
      userGroupId,
      title,
      idLegalTransactionType,
    ]
  );

  useEffect(
    () =>
      setTakenLegalClients([
        ...takenLegalClients.map(c => ({
          ...c,
          idLegalRole: availableRoles[0]?.idLegalRole,
        })),
      ]),
    [idLegalTransactionType, availableRoles]
  );

  const isFormValid =
    idLegalTransactionType !== 0 && idUser !== null && title !== '';

  const handleCreateLegalTransaction = async () => {
    if (!idUser) {
      showAlert({
        severity: 'error',
        text: 'Kein Mitarbeiter ausgewählt!',
      });
      return;
    }

    if (!legalTransactionTypeFullObject) {
      return;
    }

    try {
      const newLegalTransaction: ILegalTransactionFullObject = {
        idLegalTransactionType,
        idLegalTransactionState: 1,
        idLegalTransactionProgress: 10,
        Created_at: '',
        idLegalTransaction: -1,
        DataCollection: [],
        RelationshipArray: [],
        Title: title,
        LegalClientArray: takenLegalClients,
        LegalPhaseArray: [],
        LegalTransactionGwGArray: [],
        CertificateArray: [],
        DraftArray: [],
        OtherDocumentArray: [],
        DraftMessageArray: [],
        NoteArray: [],
        InvoiceArray: [],
        InvoiceObjectArray: [],
        Notary_idUser: idUser,
        RecordNumberArray: [],
        TransactionNumberArray: [],
        LegalRoleArray: [],
        LegalRoleCanInviteArray: [],
        Remarks: null,
        Employee_idUser: employeeIdUser,
        idUserGroup: userGroupId,
        LastPhaseUpdate_at: null,
        Closed_at: null,
        NotarizationDate: null,
        StartEditDate: null,
        // ARNOtop Sync
        idArnoTopObject:
          arnoSync.idArnoTopObject < 0 ? null : arnoSync.idArnoTopObject,
        syncWithArnoTop: arnoSync.enableLegalTransaction,
        syncAllLegalClientsWithArnotop: arnoSync.enableLegalClients,
        // Nur Intern
        DocumentArray: [],
        DocumentDirectoryArray: [],
        LegalTransactionMeetingArray: [],
        LegalTransactionMeetingEmailArray: [],
        LegalTransaction_has_LegalTransactionSpecialFunctionArray: [],
        ProcessManagerArray: [],
        Tasks: [],
        UserInformed_LackOfActivity: false,
        LastActivity_at: null,
        Measurements: legalTransactionTypeFullObject.Measurements,
      };

      const isConfirm = await handleInvoiceReminder(newLegalTransaction, theme);

      if (!isConfirm) {
        return;
      }

      const newLegalTransactionFullObject =
        await createLegalTransactionFullObject(newLegalTransaction);

      queryClient.invalidateQueries({
        queryKey: ['legaltransaction', 'search'],
      });

      if (newLegalTransactionFullObject) {
        handleFinished(newLegalTransactionFullObject.idLegalTransaction);
      }

      showAlert({
        severity: 'success',
        text: 'Vorgang erfolgreich angelegt!',
      });
    } catch {
      showAlert({
        severity: 'error',
        text: 'Es liegt ein Fehler mit der Datenverarbeitung vor! Falls dies wiederholt auftritt, kontaktieren Sie uns bitte.',
      });
    }
  };

  if (isLoadingRoleTemplates)
    return <LoadingDialog unmount={() => handleClose(false)} />;
  if (roleTemplates === undefined)
    return (
      <NoDataDialog
        unmount={() => handleClose(false)}
        refetch={refetchRoleTemplates}
      />
    );

  return (
    <Dialog open onClose={() => handleClose(isEdited)} maxWidth='lg' fullWidth>
      <DialogTitle variant='h4'>Neuer Vorgang</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }} />
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item sm={6}>
            <TextField
              label='Zugewiesener Notar'
              value={idUser === null ? '' : idUser}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setIdUser(Number(event.target.value))
              }
              error={idUser === null}
              size='small'
              fullWidth
              select
            >
              {users
                .filter(user => Boolean(user.isNotary))
                .map(user => (
                  <MenuItem
                    key={`new-idUser-${user.idUser}`}
                    value={user.idUser}
                  >
                    {user.FirstName} {user.LastName}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>
          <Grid item sm={6}>
            <Autocomplete
              options={combinedOptions}
              groupBy={option => option.type}
              getOptionLabel={option => option.name}
              style={{ width: '100%' }}
              renderInput={params => (
                <TextField
                  {...params}
                  label={
                    selectedType === 'user' ? (
                      <span>
                        <Person
                          style={{
                            verticalAlign: 'middle',
                            marginRight: 8,
                          }}
                        />
                        Zugewiesener Mitarbeiter
                      </span>
                    ) : (
                      <span>
                        <Group
                          style={{
                            verticalAlign: 'middle',
                            marginRight: 8,
                          }}
                        />
                        Zugewiesene Gruppe
                      </span>
                    )
                  }
                  size='small'
                  variant='outlined'
                />
              )}
              renderOption={(props, option) => (
                <ListItem {...props} key={option.id}>
                  <ListItemText primary={option.name} />
                </ListItem>
              )}
              onChange={(_, newValue) => {
                if (newValue) {
                  if (newValue.type === 'Mitarbeiter') {
                    setEmployeeIdUser(newValue.id);
                    setUserGroupId(null);
                    setSelectedType('user');
                  } else if (newValue.type === 'Gruppe') {
                    setEmployeeIdUser(null);
                    setUserGroupId(newValue.id);
                    setSelectedType('group');
                  }
                } else {
                  setEmployeeIdUser(null);
                  setUserGroupId(null);
                }
              }}
              value={
                selectedType === 'user' && employeeIdUser
                  ? combinedOptions.find(
                      option =>
                        option.type === 'Mitarbeiter' &&
                        option.id === employeeIdUser
                    )
                  : selectedType === 'group' && userGroupId
                    ? combinedOptions.find(
                        option =>
                          option.type === 'Gruppe' && option.id === userGroupId
                      )
                    : null
              }
            />
          </Grid>

          <Grid item sm={4}>
            <Autocomplete
              options={legalTransactionTypes}
              getOptionLabel={option => option.LegalTransactionType}
              fullWidth
              size='small'
              renderInput={params => (
                <TextField
                  {...params}
                  label='Art'
                  error={idLegalTransactionType === 0}
                  variant='outlined'
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.idLegalTransactionType}>
                  {option.LegalTransactionType}
                  {Boolean(option.withDialog) && (
                    <i> (Datenerf. auto. erstellt)</i>
                  )}
                </li>
              )}
              onChange={(_, newValue) => {
                if (newValue) {
                  setIdLegalTransactionType(newValue.idLegalTransactionType);
                  const findType = legalTransactionTypes.find(
                    legalType =>
                      legalType.idLegalTransactionType ===
                      newValue.idLegalTransactionType
                  );

                  if (
                    findType === undefined ||
                    findType.idArnoTopObject === null
                  ) {
                    setArnoSync({
                      ...arnoSync,
                      idArnoTopObject: -1,
                      enableLegalClients: false,
                      enableLegalTransaction: false,
                    });
                  } else {
                    setArnoSync({
                      ...arnoSync,
                      idArnoTopObject: findType.idArnoTopObject,
                      enableLegalClients: true,
                      enableLegalTransaction: true,
                    });
                  }
                }
              }}
              value={
                legalTransactionTypes.find(
                  ltt => ltt.idLegalTransactionType === idLegalTransactionType
                ) || null
              }
            />
          </Grid>

          <Grid item sm={8}>
            <TextField
              label='Bezeichnung'
              value={title}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setTitle(event.target.value)
              }
              error={title === ''}
              size='small'
              fullWidth
            />
          </Grid>
        </Grid>

        {config.App.includes('arno_top') && (
          <Card variant='outlined' sx={{ mt: 3, mb: 3 }}>
            <CardContent>
              <Typography>ArnoTop-Synchronisation</Typography>
              <Grid container>
                <Grid xs={5}>
                  <FormGroup row>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={arnoSync.enableLegalTransaction}
                          onChange={() =>
                            setArnoSync({
                              ...arnoSync,
                              enableLegalTransaction:
                                !arnoSync.enableLegalTransaction,
                            })
                          }
                        />
                      }
                      label='Vorgang an ArnoTop'
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={arnoSync.enableLegalClients}
                          onChange={() =>
                            setArnoSync({
                              ...arnoSync,
                              enableLegalClients: !arnoSync.enableLegalClients,
                            })
                          }
                        />
                      }
                      label='Mandanten an ArnoTop'
                    />
                  </FormGroup>
                </Grid>

                <Grid xs={4}>
                  <Collapse
                    in={
                      arnoSync.enableLegalTransaction &&
                      arnoSync.idArnoTopObject < 0
                    }
                  >
                    <Alert severity='warning' sx={{ mr: 2, mt: -0.5 }}>
                      Gegenstand wählen!
                    </Alert>
                  </Collapse>
                </Grid>

                <Grid xs={3}>
                  {arnoTopObjects !== undefined ? (
                    <CustomeTextField
                      attr='idArnoTopObject'
                      label='ArnoTop Gegenstand'
                      object={
                        arnoSync.idArnoTopObject === -1
                          ? { ...arnoSync, idArnoTopObject: null }
                          : arnoSync
                      }
                      setObject={(updated: IArnoTopObject) =>
                        setArnoSync({
                          idArnoTopObject: updated.idArnoTopObject,
                          enableLegalClients: updated.idArnoTopObject !== 1,
                          enableLegalTransaction: updated.idArnoTopObject !== 1,
                        })
                      }
                      type='select'
                      required
                      null={arnoSync.idArnoTopObject === -1}
                    >
                      {arnoTopObjects
                        ?.sort((a, b) =>
                          a.ArnoTopObject < b.ArnoTopObject ? -1 : 1
                        )
                        .map(arno => (
                          <MenuItem
                            key={`arnotop-object-${arno.idArnoTopObject}`}
                            value={arno.idArnoTopObject}
                          >
                            {arno.ArnoTopObject}
                          </MenuItem>
                        ))}
                    </CustomeTextField>
                  ) : (
                    <Alert severity='warning'>
                      ArnoTop-Gegestände konnten nicht bezogen werden!
                    </Alert>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        <LegalTransactionClientsOverview
          legalRoles={availableRoles}
          takenLegalClients={takenLegalClients}
          setTakenLegalClients={setTakenLegalClients}
        />
      </DialogContent>

      <DialogActions>
        <Button variant='outlined' onClick={() => handleClose(isEdited)}>
          Abbruch
        </Button>
        <Button
          variant='contained'
          onClick={handleCreateLegalTransaction}
          disabled={
            !isFormValid ||
            isPendingCreateLegalTransactionFullObject ||
            !legalTransactionTypeFullObject ||
            (arnoSync.enableLegalTransaction && arnoSync.idArnoTopObject < 0)
          }
        >
          {isPendingCreateLegalTransactionFullObject ? (
            <Box display='flex' alignItems='center' gap={1}>
              <span>Vorgang anlegen</span>
              <CircularProgress size={20} />
            </Box>
          ) : (
            'Vorgang anlegen'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
